import React, { useEffect, useState } from "react";
import { Menu } from "antd";
import {
  HomeOutlined,
  RobotOutlined,
  FileSearchOutlined,
  CalendarOutlined,
  TableOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { RolesEnum } from "../services/rolesEnum";
import { isAuthorized } from "../services/authService";

import icon1 from "../Assets/svg/iconMenu1.svg";
import icon2 from "../Assets/svg/iconMenu2.svg";
import icon3 from "../Assets/svg/iconMenu3.svg";
import icon4 from "../Assets/svg/iconMenu4.svg";
import icon5 from "../Assets/svg/iconMenu5.svg";
import icon6 from "../Assets/svg/iconMenu6.svg";
import icon7 from "../Assets/svg/iconMenu7.svg";
import backToWorkspace from "../Assets/svg/backToWorkspace.svg";
import menu3 from "../Assets/svg/menu3.svg";
import AuthService from "../services/authentication.service";

const isGuestShare = process.env.REACT_APP_API_IS_GUEST;

// F-01 — the sidebar hides sections a role can't access via the static
// isAuthorized() gating below ("hidden, not disabled"). Manifest-driven
// filtering was removed: it hid valid menus whenever the backend homeManifest
// route format didn't match the static routes. Re-introduce it only once the
// real homeManifest `modules[].route` format is confirmed.
const SidebarMenu = ({ collapsed = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [selectedKey, setSelectedKey] = useState(null);
  const [openKeys, setOpenKeys] = useState([]);
  const [loading, setLoading] = useState(false);

  // The lifecycle sections that hang off the "Project Management" group below.
  // Collapsing the group hides all of them, which leaves Audit Trail as the only
  // section still on screen.
  const phaseSections = [
    isAuthorized([
      RolesEnum.ProjectManager,
      RolesEnum.SystemAdmin,
      RolesEnum.Guest,
    ]) && {
      key: "4",
      label: "Planning",
      icon: <img src={icon2} />,
      children: [
        isAuthorized([
          RolesEnum.ProjectManager,
          RolesEnum.SystemAdmin,
          RolesEnum.Guest,
        ]) && {
          key: "4-ch-1",
          label: "IP Planning Board",
          path: "/project/planning?ip-planning",
        },
        isAuthorized([
          RolesEnum.ProjectManager,
          RolesEnum.SystemAdmin,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldEnumerator,
          RolesEnum.FieldLogistics,
          RolesEnum.Guest,
          RolesEnum.LimitedGuest,
          RolesEnum.DataAnalyst,
        ]) && {
          key: "4-ch-2",
          label: "Visits Board",
          path: "/project/planning?visits",
        },
        isAuthorized([
          RolesEnum.ProjectManager,
          RolesEnum.SystemAdmin,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldLogistics,
          RolesEnum.DataAnalyst,
          RolesEnum.FieldTeamLeader,
          RolesEnum.Guest,
        ]) && {
          key: "4-ch-3",
          label: "Tasks Assignments",
          path: "/project/planning?tasks",
        },
        isAuthorized([
          RolesEnum.ProjectManager,
          RolesEnum.SystemAdmin,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldLogistics,
          RolesEnum.DataAnalyst,
          RolesEnum.FieldTeamLeader,
          RolesEnum.Guest,
          RolesEnum.FieldEnumerator,
        ]) && {
          key: "4-ch-4",
          label: "Field Logistics Board",
          path: "/project/planning?logistics",
        },
        isAuthorized([
          RolesEnum.ProjectManager,
          RolesEnum.SystemAdmin,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldLogistics,
          RolesEnum.DataAnalyst,
          RolesEnum.FieldTeamLeader,
          RolesEnum.Guest,
          RolesEnum.FieldEnumerator,
        ]) && {
          key: "4-ch-5",
          label: "Facilities",
          path: "/project/planning?facilities",
        },
        isAuthorized([
          RolesEnum.ProjectManager,
          RolesEnum.SystemAdmin,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldLogistics,
          RolesEnum.DataAnalyst,
          RolesEnum.FieldTeamLeader,
          RolesEnum.Guest,
          RolesEnum.FieldEnumerator,
        ]) && {
          key: "4-ch-6",
          label: "Additional Tools",
          path: "/project/planning?sample-size",
        },
      ],
    },
    isAuthorized([
      RolesEnum.ProjectManager,
      RolesEnum.SystemAdmin,
      RolesEnum.FieldCoordinator,
      RolesEnum.FieldTeamLeader,
      RolesEnum.FieldEnumerator,
      RolesEnum.FieldLogistics,
      RolesEnum.DataAnalyst,
      RolesEnum.Guest,
    ]) && {
      key: "5",
      label: "Implementation",
      icon: <img src={icon3} />,
      children: [
        {
          key: "5-ch-1",
          label: "Visits Board",
          children: [
            {
              key: "5-ch-1-sub-1",
              label: "Waiting for Tracking",
              path: "/project/implementation?WaitingForTracking",
            },
            {
              key: "5-ch-1-sub-2",
              label: "Tracking in Progress",
              path: "/project/implementation?trackingInprogress",
            },
          ],
        },
        {
          key: "5-ch-2",
          label: "Monitoring Round Overview",
          path: "/project/implementation?MonitoringRoundOverview",
        },
      ],
    },
    {
      key: "6",
      label: "Reporting",
      icon: <img src={icon4} />,
      children: [
        isAuthorized([
          RolesEnum.ProjectManager,
          RolesEnum.SystemAdmin,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldTeamLeader,
          RolesEnum.FieldEnumerator,
          RolesEnum.FieldLogistics,
          RolesEnum.DataAnalyst,
        ]) && {
          key: "6-ch-1",
          label: "Field Team Status Board",
          path: "/project/reporting?FieldTeamStatusBoard",
        },
        isAuthorized([
          RolesEnum.ProjectManager,
          RolesEnum.SystemAdmin,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldTeamLeader,
          RolesEnum.FieldEnumerator,
          RolesEnum.FieldLogistics,
          RolesEnum.DataAnalyst,
        ]) && {
          key: "6-ch-2",
          label: "IP Donor Status Board",
          path: "/project/reporting?IpDonorStatusBoard",
        },
        isAuthorized([
          RolesEnum.ProjectManager,
          RolesEnum.SystemAdmin,
          RolesEnum.FieldCoordinator,
          RolesEnum.FieldTeamLeader,
          RolesEnum.FieldLogistics,
          RolesEnum.DataAnalyst,
        ]) && {
          key: "6-ch-3",
          label: "Visit Board Reporting List",
          children: [
            {
              key: "6-ch-3-sub-1",
              label: "Site Visit Reporting List",
              path: "/project/reporting?siteVisitReportingList",
            },
            {
              key: "6-ch-3-sub-2",
              label: "Submitted for Approval",
              path: "/project/reporting?submittedForApproval",
            },
          ],
        },
        {
          key: "6-ch-4",
          label: "Data Checks",
          path: "/project/reporting?DataChecks",
        },
        isAuthorized([RolesEnum.ProjectManager, RolesEnum.SystemAdmin]) && {
          key: "6-ch-5",
          label: "Approve Site Visits",
          children: [
            {
              key: "6-ch-5-sub-1",
              label: "Submitted for Approval",
              path: "/project/reporting?SubmittedForApproval",
            },
            {
              key: "6-ch-5-sub-2",
              label: "Approved Site Visits",
              path: "/project/reporting?ApprovedVisits",
            },
          ],
        },
      ],
    },
    isAuthorized([
      RolesEnum.ProjectManager,
      RolesEnum.SystemAdmin,
      RolesEnum.FieldCoordinator,
      RolesEnum.Guest,
      RolesEnum.LimitedGuest,
    ]) && {
      key: "7",
      label: "Completed",
      icon: <img src={icon5} />,
      children: [
        {
          key: "7-ch-1",
          label: "Completed Site Visits",
          path: "/project/completed?ReportingList",
        },
        isAuthorized([
          RolesEnum.ProjectManager,
          RolesEnum.SystemAdmin,
          RolesEnum.Guest,
          RolesEnum.FieldCoordinator,
        ]) && {
          key: "7-ch-2",
          label: "Dataset List",
          path: "/project/completed?DatasetList",
        },
        {
          key: "7-ch-3",
          label: "Visits Map",
          path: "/project/completed?VisitsMap",
        },
      ],
    },
    isAuthorized([
      RolesEnum.ProjectManager,
      RolesEnum.SystemAdmin,
      RolesEnum.FieldCoordinator,
      RolesEnum.FieldTeamLeader,
      RolesEnum.FieldEnumerator,
      RolesEnum.FieldLogistics,
      RolesEnum.DataAnalyst,
    ]) && {
      key: "8",
      label: "Flags",
      icon: <img src={icon6} />,
      children: [
        {
          key: "8-ch-1",
          label: "Red Flags Board",
          path: "/project/flags?Flags",
        },
        {
          key: "8-ch-2",
          label: "Post-Visit Card Board",
          path: "/project/flags?PostVisits",
        },
      ],
    },
  ];

  const projectManagementMenuItems = [
    {
      key: "project-management",
      label: "Project Management",
      className: "projectManagement",
      icon: (
        <AppstoreOutlined />
      ),
      children: phaseSections,
    },
    isAuthorized([RolesEnum.ProjectManager, RolesEnum.SystemAdmin]) && {
      key: "audit",
      label: "Audit Trail",
      icon: (
        <FileSearchOutlined />
      ),
      path: "/project/audit",
    },
    {
      key: "9",
      label: "Logout",
      className: "logoutMenu",
      icon: (
        <img src={icon7} alt="Logout Icon" />
      ),
    },
  ];

  const menuItems = [
    {
      key: "1",
      label: "Back to Projects",
      path: "/projects",
      icon: <img src={backToWorkspace} />,
    },
    !isGuestShare && {
      key: "home",
      label: "Home",
      path: "/project/home",
      icon: <HomeOutlined />,
    },
    // AI Assistant — available to every authenticated role except guests.
    !isGuestShare &&
      isAuthorized([
        RolesEnum.ProjectManager,
        RolesEnum.SystemAdmin,
        RolesEnum.FieldCoordinator,
        RolesEnum.FieldLogistics,
        RolesEnum.FieldTeamLeader,
        RolesEnum.FieldEnumerator,
        RolesEnum.DataAnalyst,
      ]) && {
        key: "ai",
        label: "AI Assistant",
        icon: (
          <RobotOutlined />
        ),
        path: "/project/ai",
      },
    // Calendar — promoted to a top-level entry (also still a Home tab). The
    // unified planning calendar with drag-to-reschedule.
    !isGuestShare && {
      key: "calendar",
      label: "Calendar",
      path: "/project/calendar",
      icon: (
        <CalendarOutlined />
      ),
    },
    // Site Visit Summary — the cross-lifecycle list of every site visit, with
    // the main fields (name, status, target date, facility) editable in place.
    // Also embedded as a Home tab; this is the direct entry point.
    !isGuestShare &&
      isAuthorized([
        RolesEnum.ProjectManager,
        RolesEnum.SystemAdmin,
        RolesEnum.FieldCoordinator,
        RolesEnum.FieldLogistics,
        RolesEnum.FieldTeamLeader,
        RolesEnum.FieldEnumerator,
        RolesEnum.DataAnalyst,
        RolesEnum.Guest,
      ]) && {
        key: "site-visit-summary",
        label: "Site Visit Summary",
        path: "/project/site-visit-summary",
        icon: (
          <TableOutlined />
        ),
      },
    !isGuestShare && {
      key: "2",
      label: "IP Management",
      path: "/project/ip-management",
      icon: <img src={menu3} />,
    },
    // Dashboard was merged into the Home page for the main app; it remains a
    // separate entry only in the guest-share experience (no Home route there).
    isGuestShare && {
      key: "3",
      label: "Dashboard",
      path: "/project/dashboard",
      icon: <img src={icon1} />,
      style: { marginBottom: "20px" },
    },
    ...(isGuestShare ? [] : projectManagementMenuItems),
  ];

  const setSelectedMenu = (url, items, parentKeys = []) => {
    for (let item of items) {
      if (item?.children?.length) {
        const found = setSelectedMenu(url, item.children, [
          ...parentKeys,
          item.key,
        ]);
        if (found) return true;
      }
      if (item?.path === url) {
        const openPathKeys = [...parentKeys];
        setOpenKeys(openPathKeys);
        setSelectedKey(item.key);
        return true;
      }
    }
    return false;
  };

  useEffect(() => {
    const url = location.pathname + location.search;
    setSelectedMenu(url, menuItems);
  }, [location]);

  /**
   * Keys of every submenu above `key`, outermost first — e.g. the Visits Board
   * inside Implementation returns ["project-management", "5"].
   */
  const ancestorKeys = (key, items = menuItems, chain = []) => {
    for (const item of items.filter(Boolean)) {
      if (item?.key === key) return chain;
      if (Array.isArray(item?.children)) {
        const found = ancestorKeys(key, item.children, [...chain, item.key]);
        if (found) return found;
      }
    }
    return null;
  };

  /**
   * One open branch at a time, at every level.
   *
   * Opening a section keeps its ancestors open and closes its siblings, so
   * picking Reporting collapses Planning but leaves Project Management expanded.
   * Closing a section also closes whatever was open inside it.
   *
   * This replaces a numeric/non-numeric key split that could only ever hold two
   * levels open — the Project Management group added a third.
   */
  const handleMenuChange = (keys) => {
    const opened = keys.find((key) => !openKeys.includes(key));

    if (opened) {
      setOpenKeys([...(ancestorKeys(opened) || []), opened]);
      return;
    }

    const closed = openKeys.find((key) => !keys.includes(key));
    if (!closed) {
      setOpenKeys(keys);
      return;
    }
    setOpenKeys(
      openKeys.filter(
        (key) => key !== closed && !(ancestorKeys(key) || []).includes(closed)
      )
    );
  };

  const handleLogout = async () => {
    setLoading(true);
    AuthService.logout();
  };

  /**
   * The tree above, as antd's `items`.
   *
   * This used to render <Menu.Item> children with the icon and the label both
   * inside a <Link>. That cannot collapse: in icons-only mode antd hides the
   * label (which held the link) and keeps the `icon` slot, so the visible icon
   * would not have been clickable. Icons now live in the slot antd keeps, and
   * navigation is the item's own onClick — which works in both modes.
   */
  const toMenuItems = (items = []) =>
    items
      .filter(Boolean)
      .map((item) => {
        // Only count children the current role can actually see, so a parent
        // whose children are all role-gated away is hidden entirely rather than
        // shown as an empty submenu (F-01: hidden, not disabled).
        const visibleChildren = Array.isArray(item?.children)
          ? item.children.filter(Boolean)
          : [];

        if (visibleChildren.length > 0) {
          return {
            key: item.key,
            icon: item.icon,
            label: item.label,
            className: item.className || "",
            children: toMenuItems(visibleChildren),
          };
        }

        // A parent that has a children array but no visible children and no path
        // of its own is not navigable for this role — hide it.
        if (Array.isArray(item?.children) && !item.path) {
          return null;
        }

        const isLogout = item.label === "Logout";

        return {
          key: item.key,
          icon: item.icon,
          label: item.label,
          style: item.style,
          className:
            location.pathname + location.search === item.path
              ? "costum-menu-item-active"
              : item.className || "",
          disabled: isLogout && loading,
          onClick: isLogout
            ? handleLogout
            : item.path
            ? () => navigate(item.path)
            : undefined,
        };
      })
      .filter(Boolean);

  return (
    <Menu
      theme="dark"
      mode="inline"
      inlineCollapsed={collapsed}
      // Collapsed, the submenus are hover popups — antd owns their open state
      // there, so the one-branch-at-a-time rule applies to the open menu only.
      openKeys={collapsed ? undefined : openKeys}
      onOpenChange={collapsed ? undefined : handleMenuChange}
      selectedKeys={[selectedKey]}
      className="menu"
      items={toMenuItems(menuItems)}
    />
  );
};

export default SidebarMenu;
